import React, { useState, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MapIcon from "@mui/icons-material/Map";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const TablaRutas = ({ rutas = [] }) => {
  const [search, setSearch] = useState("");
  const [copiedLink, setCopiedLink] = useState(null);

  const copiarAlPortapapeles = (texto) => {
    navigator.clipboard.writeText(texto);
    setCopiedLink(texto);
    setTimeout(() => setCopiedLink(null), 1500); // Restablece después de 1.5s
  };

  const filteredRows = useMemo(() => {
    const hoy = dayjs().startOf("day");
    const pasadoManana = dayjs().add(2, "day").endOf("day");

    return rutas
      .filter((ruta) => {
        const fechaRuta = dayjs(ruta.insert_date);
        return fechaRuta.isBetween(hoy, pasadoManana, null, "[]"); // incluye límites
      })
      .filter((ruta) =>
        `${ruta.name} ${ruta.driver} ${ruta.vehicle} ${ruta.notes}`
          .toLowerCase()
          .includes(search.toLowerCase())
      );
  }, [rutas, search]);

  const columns = [
    {
      field: "company",
      headerName: "Compañia",
      flex: 1,
      minWidth: 70,
      sortable: true,
    },
    {
      field: "name",
      headerName: "Nombre de Ruta",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "vehicle",
      headerName: "# Unidad",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "driver",
      headerName: "Conductor",
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "notes",
      headerName: "Notas",
      flex: 2,
      minWidth: 200,
      sortable: false,
    },
    {
      field: "insert_date",
      headerName: "Fecha",
      flex: 1,
      minWidth: 150,
      valueFormatter: (params) => dayjs(params).format("DD/MM/YYYY HH:mm"),
      sortable: true,
    },
    {
      field: "trace_view_link",
      headerName: "Link Vista",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver Ruta">
            <IconButton onClick={() => window.open(params.value, "_blank")}>
              <MapIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={copiedLink === params.value ? "¡Copiado!" : "Copiar"}>
            <IconButton onClick={() => copiarAlPortapapeles(params.value)}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "navigation_link",
      headerName: "Link Navegación",
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Navegar">
            <IconButton onClick={() => window.open(params.value, "_blank")}>
              <LaunchIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title={copiedLink === params.value ? "¡Copiado!" : "Copiar"}>
            <IconButton onClick={() => copiarAlPortapapeles(params.value)}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
        Rutas
      </Typography>

      <TextField
        variant="outlined"
        fullWidth
        placeholder="Buscar..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ height: 420, width: "100%" }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          getRowId={(row) => row.route_id}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[15]}
          disableSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default TablaRutas;
